package app.smartcoding.realrate.presentation.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.animateColorAsState
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import app.smartcoding.realrate.domain.model.CreditInputs
import app.smartcoding.realrate.domain.model.creditCardField.LoanField
import app.smartcoding.realrate.domain.model.creditCardField.MonthsField
import app.smartcoding.realrate.domain.model.creditCardField.PaymentField
import app.smartcoding.realrate.localization.LocalStrings

@Composable
fun InputFields(
    inputs: CreditInputs,
    onLoanFieldsChanged: (LoanField) -> Unit,
    onToggleAmountDetails: () -> Unit,
    onToggleLockedTaxRate: () -> Unit,
    onMonthsFieldChanged: (MonthsField) -> Unit,
    onToggleMountsDetails: () -> Unit,
    onPaymentFieldsChanged: (PaymentField) -> Unit,
    onTogglePaymentDetails: () -> Unit
) {
    val colorLoanTotal by animateColorAsState(
        targetValue = if (inputs.showDetailedLoanAmountInput) MaterialTheme.colorScheme.surfaceContainerLowest else MaterialTheme.colorScheme.surfaceContainerLow,
    )
    val colorTermsMonthsTotal by animateColorAsState(
        targetValue = if (inputs.showDetailedMountsInput) MaterialTheme.colorScheme.surfaceContainerLowest else MaterialTheme.colorScheme.surfaceContainerLow,
    )
    val colorPaymentTotal by animateColorAsState(
        targetValue = if (inputs.showDetailedPaymentInput) MaterialTheme.colorScheme.surfaceContainerLowest else MaterialTheme.colorScheme.surfaceContainerLow,
    )

    Column(
        modifier = Modifier
            .clip(shape = MaterialTheme.shapes.large)
            .border(
                width = 1.dp,
                color = MaterialTheme.colorScheme.outlineVariant,
                shape = MaterialTheme.shapes.large
            )
    ) {
        Column(
            modifier = Modifier
                .background(color = colorLoanTotal)
                .padding(
                    start = 16.dp,
                    end = 16.dp,
                    top = if (inputs.showDetailedLoanAmountInput) 16.dp else 16.dp,
                    bottom = if (inputs.showDetailedLoanAmountInput) 16.dp else 8.dp
                )
        ) {
            InputField(
                textFieldValue = inputs.loanRequestedAmount,
                onValueChange = { onLoanFieldsChanged(LoanField.RequestedAmount(it, 0.0)) },
                label = LocalStrings.current.totalCreditAmount,
                onIncrement = { onLoanFieldsChanged(LoanField.RequestedAmount(it, 100.0)) },
                onDecrement = { onLoanFieldsChanged(LoanField.RequestedAmount(it, -100.0)) },
                onToggleExpand = onToggleAmountDetails,
                isExpanded = inputs.showDetailedLoanAmountInput,
                isError = inputs.loanRequestedAmount.text.isBlank(),
                prefix = { Text(text = LocalStrings.current.currencySymbol, maxLines = 1) }
            )
            AnimatedVisibility(inputs.showDetailedLoanAmountInput) {
                Column {
                    Spacer(modifier = Modifier.height(16.dp))
                    InputField(
                        textFieldValue = inputs.loanTaxFee,
                        onValueChange = { onLoanFieldsChanged(LoanField.TaxFee(it, 0.0)) },
                        label = LocalStrings.current.prepaidFeeFixed,
                        onIncrement = { onLoanFieldsChanged(LoanField.TaxFee(it, 100.0)) },
                        onDecrement = { onLoanFieldsChanged(LoanField.TaxFee(it, -100.0)) },
                        isError = inputs.loanTaxFee.text.isBlank(),
                        prefix = { Text(text = LocalStrings.current.currencySymbol, maxLines = 1) }
                    )
                    Spacer(modifier = Modifier.height(16.dp))
                    InputField(
                        textFieldValue = inputs.loanTaxRate,
                        onValueChange = { onLoanFieldsChanged(LoanField.TaxRate(it, 0.0)) },
                        label = LocalStrings.current.prepaidFeePercentage,
                        onIncrement = { onLoanFieldsChanged(LoanField.TaxRate(it, 0.5)) },
                        onDecrement = { onLoanFieldsChanged(LoanField.TaxRate(it, -0.5)) },
                        onToggleLockedTaxRate = onToggleLockedTaxRate,
                        isLockedTaxRate = inputs.isLockedTaxRate,
                        isError = inputs.loanTaxRate.text.isBlank(),
                        prefix = { Text(text = "%", maxLines = 1) },
                    )
                    Spacer(modifier = Modifier.height(16.dp))
                    InputField(
                        textFieldValue = inputs.loanReceivedAmount,
                        onValueChange = { onLoanFieldsChanged(LoanField.ReceivedAmount(it, 0.0)) },
                        label = LocalStrings.current.netAmountDisbursed,
                        onIncrement = { onLoanFieldsChanged(LoanField.ReceivedAmount(it, 100.0)) },
                        onDecrement = { onLoanFieldsChanged(LoanField.ReceivedAmount(it, -100.0)) },
                        onToggleExpand = onToggleAmountDetails,
                        isError = inputs.loanReceivedAmount.text.isBlank(),
                        prefix = { Text(text = LocalStrings.current.currencySymbol, maxLines = 1) }
                    )
                }
            }
        }
        Column(
            modifier = Modifier
                .background(colorTermsMonthsTotal)
                .padding(
                    start = 16.dp,
                    end = 16.dp,
                    top = if (inputs.showDetailedMountsInput) 16.dp else 4.dp,
                    bottom = if (inputs.showDetailedMountsInput) 16.dp else 8.dp
                )
        ) {
            InputField(
                textFieldValue = inputs.termsMonthsTotal,
                onValueChange = { onMonthsFieldChanged(MonthsField.MonthsTotal(it, 0.0)) },
                label = LocalStrings.current.loanTermTotalMonths,
                onIncrement = { onMonthsFieldChanged(MonthsField.MonthsTotal(it, 1.0)) },
                onDecrement = { onMonthsFieldChanged(MonthsField.MonthsTotal(it, -1.0)) },
                isExpanded = inputs.showDetailedMountsInput,
                onToggleExpand = onToggleMountsDetails,
                isError = inputs.termsMonthsTotal.text.isBlank(),
            )
            AnimatedVisibility(inputs.showDetailedMountsInput) {
                Column {
                    Spacer(modifier = Modifier.height(16.dp))
                    InputField(
                        textFieldValue = inputs.termsYears,
                        onValueChange = { onMonthsFieldChanged(MonthsField.Years(it, 0.0)) },
                        label = LocalStrings.current.years,
                        onIncrement = { onMonthsFieldChanged(MonthsField.Years(it, 1.0)) },
                        onDecrement = { onMonthsFieldChanged(MonthsField.Years(it, -1.0)) },
                        isError = inputs.termsYears.text.isBlank(),
                    )
                    Spacer(modifier = Modifier.height(16.dp))
                    InputField(
                        textFieldValue = inputs.termsMonths,
                        onValueChange = { onMonthsFieldChanged(MonthsField.Months(it, 0.0)) },
                        label = LocalStrings.current.months,
                        onIncrement = { onMonthsFieldChanged(MonthsField.Months(it, 1.0)) },
                        onDecrement = { onMonthsFieldChanged(MonthsField.Months(it, -1.0)) },
                        isError = inputs.termsMonths.text.isBlank(),
                    )
                }
            }
        }
        Column(
            modifier = Modifier
                .background(color = colorPaymentTotal)
                .padding(
                    start = 16.dp,
                    end = 16.dp,
                    top = if (inputs.showDetailedPaymentInput) 16.dp else 4.dp,
                    bottom = if (inputs.showDetailedPaymentInput) 16.dp else 16.dp
                )
        ) {
            InputField(
                textFieldValue = inputs.paymentTotal,
                onValueChange = { onPaymentFieldsChanged(PaymentField.PaymentTotal(it, 0.0)) },
                label = LocalStrings.current.monthlyInstallment,
                onIncrement = { onPaymentFieldsChanged(PaymentField.PaymentTotal(it, 1.0)) },
                onDecrement = { onPaymentFieldsChanged(PaymentField.PaymentTotal(it, -1.0)) },
                isExpanded = inputs.showDetailedPaymentInput,
                onToggleExpand = onTogglePaymentDetails,
                isError = inputs.paymentTotal.text.isBlank(),
                prefix = { Text(text = LocalStrings.current.currencySymbol, maxLines = 1) }
            )
            AnimatedVisibility(inputs.showDetailedPaymentInput) {
                Column {
                    Spacer(modifier = Modifier.height(16.dp))
                    InputField(
                        textFieldValue = inputs.paymentInsurance,
                        onValueChange = { onPaymentFieldsChanged(PaymentField.Insurance(it, 0.0)) },
                        label = LocalStrings.current.otherFees,
                        onIncrement = { onPaymentFieldsChanged(PaymentField.Insurance(it, 1.0)) },
                        onDecrement = { onPaymentFieldsChanged(PaymentField.Insurance(it, -1.0)) },
                        isError = inputs.paymentInsurance.text.isBlank(),
                        prefix = { Text(text = LocalStrings.current.currencySymbol, maxLines = 1) }
                    )
                    Spacer(modifier = Modifier.height(16.dp))
                    InputField(
                        textFieldValue = inputs.paymentFees,
                        onValueChange = { onPaymentFieldsChanged(PaymentField.Fees(it, 0.0)) },
                        label = LocalStrings.current.insuranceCost,
                        onIncrement = { onPaymentFieldsChanged(PaymentField.Fees(it, 1.0)) },
                        onDecrement = { onPaymentFieldsChanged(PaymentField.Fees(it, -1.0)) },
                        isError = inputs.paymentFees.text.isBlank(),
                        prefix = { Text(text = LocalStrings.current.currencySymbol, maxLines = 1) }
                    )
                    Spacer(modifier = Modifier.height(16.dp))
                    InputField(
                        textFieldValue = inputs.paymentMain,
                        onValueChange = {
                            onPaymentFieldsChanged(
                                PaymentField.MainPayment(
                                    it,
                                    0.0
                                )
                            )
                        },
                        label = LocalStrings.current.principalInstallment,
                        onIncrement = { onPaymentFieldsChanged(PaymentField.MainPayment(it, 1.0)) },
                        onDecrement = {
                            onPaymentFieldsChanged(
                                PaymentField.MainPayment(
                                    it,
                                    -1.0
                                )
                            )
                        },
                        isError = inputs.paymentMain.text.isBlank(),
                        prefix = { Text(text = LocalStrings.current.currencySymbol, maxLines = 1) }
                    )
                }
            }
        }
    }
}